// Collapsible JSON viewer, Monaco-style: a flat list of lines with a left
// gutter that holds fold controls next to lines that open an object/array.
// Folding replaces the multi-line block with a single inline summary line
// ("{ ... 3 keys }"). No <details>/<summary> — open state lives in Lit so
// re-renders preserve clicks, and line layout is fully under our control.
//
// Props:
//   data      — the value to render (any JSON-compatible value)
//   openDepth — depth at which nodes start expanded (default 1).
//               0 = everything collapsed, 1 = top level open, etc.

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

const INDENT = '  ';

class JsonTree extends BaseComponent {
  static properties = {
    data: { attribute: false },
    openDepth: { type: Number, attribute: 'open-depth' },
    _open: { state: true },
  };

  static styles = css`
    :host {
      display: block;
      font-family: var(--bs-font-monospace, monospace);
      font-size: 0.8125rem;
      line-height: 1.35;
      color: #212529;
    }
    .line {
      display: flex;
      align-items: baseline;
    }
    .gutter {
      flex: 0 0 0.9rem;
      color: #6c757d;
      font-size: 0.6rem;
      text-align: center;
      user-select: none;
    }
    .gutter.foldable { cursor: pointer; }
    .gutter.foldable:hover { color: #212529; }
    .content {
      flex: 1 1 auto;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .preview { color: #6c757d; font-style: italic; }
    .punct { color: #6c757d; }
    .key { color: #a0522d; }
    .v-string { color: #008000; }
    .v-number { color: #0d6efd; }
    .v-bool { color: #b22222; }
    .v-null { color: #b22222; font-style: italic; }
  `;

  constructor() {
    super();
    this.data = undefined;
    this.openDepth = 1;
    /** @type {Record<string, boolean>} */
    this._open = {};
  }

  /**
   * @param {string} path
   * @param {number} depth
   * @returns {boolean}
   */
  _isOpen(path, depth) {
    if (Object.hasOwn(this._open, path)) {
      return this._open[path];
    }
    return depth < this.openDepth;
  }

  /** @param {string} path @param {number} depth */
  _toggle(path, depth) {
    this._open = { ...this._open, [path]: !this._isOpen(path, depth) };
  }

  render() {
    const lines = this._renderValue(this.data, '$', 0, 0);
    return html`
      ${lines.map(
        (l) => html`
          <div class="line">
            <span
              class="gutter ${l.foldPath ? 'foldable' : ''}"
              @click="${l.foldPath ? () => this._toggle(l.foldPath, l.foldDepth || 0) : null}"
            >${l.foldPath ? (l.folded ? '▶' : '▼') : ''}</span>
            <span class="content">${INDENT.repeat(l.indent)}${l.content}</span>
          </div>
        `,
      )}
    `;
  }

  /**
   * @typedef {{ indent: number, content: import('lit').TemplateResult | string, foldPath?: string, foldDepth?: number, folded?: boolean }} Line
   * @param {*} value
   * @param {string} path
   * @param {number} depth
   * @param {number} indent
   * @returns {Array<Line>}
   */
  _renderValue(value, path, depth, indent) {
    if (value === null) return [{ indent, content: html`<span class="v-null">null</span>` }];
    if (Array.isArray(value)) return this._renderArray(value, path, depth, indent);
    const t = typeof value;
    if (t === 'object') return this._renderObject(/** @type {Record<string, unknown>} */ (value), path, depth, indent);
    if (t === 'string') return [{ indent, content: html`<span class="v-string">${JSON.stringify(value)}</span>` }];
    if (t === 'number') return [{ indent, content: html`<span class="v-number">${value}</span>` }];
    if (t === 'boolean') return [{ indent, content: html`<span class="v-bool">${String(value)}</span>` }];
    return [{ indent, content: html`<span>${JSON.stringify(value)}</span>` }];
  }

  /**
   * @param {Record<string, unknown>} obj
   * @param {string} path
   * @param {number} depth
   * @param {number} indent
   * @returns {Array<Line>}
   */
  _renderObject(obj, path, depth, indent) {
    const keys = Object.keys(obj);
    if (keys.length === 0) return [{ indent, content: html`<span class="punct">{}</span>` }];
    if (!this._isOpen(path, depth)) {
      return [
        {
          indent,
          content: html`<span class="punct">{</span> <span class="preview">${keys.length} ${keys.length === 1 ? 'key' : 'keys'}</span> <span class="punct">}</span>`,
          foldPath: path,
          foldDepth: depth,
          folded: true,
        },
      ];
    }
    /** @type {Array<Line>} */
    const lines = [
      {
        indent,
        content: html`<span class="punct">{</span>`,
        foldPath: path,
        foldDepth: depth,
        folded: false,
      },
    ];
    keys.forEach((k, i) => {
      const isLast = i === keys.length - 1;
      const childLines = this._renderValue(obj[k], `${path}.${k}`, depth + 1, indent + 1);
      const first = childLines[0];
      first.content = html`<span class="key">${JSON.stringify(k)}</span><span class="punct">: </span>${first.content}`;
      if (!isLast) {
        const last = childLines[childLines.length - 1];
        last.content = html`${last.content}<span class="punct">,</span>`;
      }
      lines.push(...childLines);
    });
    lines.push({ indent, content: html`<span class="punct">}</span>` });
    return lines;
  }

  /**
   * @param {Array<unknown>} arr
   * @param {string} path
   * @param {number} depth
   * @param {number} indent
   * @returns {Array<Line>}
   */
  _renderArray(arr, path, depth, indent) {
    if (arr.length === 0) return [{ indent, content: html`<span class="punct">[]</span>` }];
    if (!this._isOpen(path, depth)) {
      return [
        {
          indent,
          content: html`<span class="punct">[</span> <span class="preview">${arr.length} ${arr.length === 1 ? 'item' : 'items'}</span> <span class="punct">]</span>`,
          foldPath: path,
          foldDepth: depth,
          folded: true,
        },
      ];
    }
    /** @type {Array<Line>} */
    const lines = [
      {
        indent,
        content: html`<span class="punct">[</span>`,
        foldPath: path,
        foldDepth: depth,
        folded: false,
      },
    ];
    arr.forEach((item, i) => {
      const isLast = i === arr.length - 1;
      const childLines = this._renderValue(item, `${path}[${i}]`, depth + 1, indent + 1);
      if (!isLast) {
        const last = childLines[childLines.length - 1];
        last.content = html`${last.content}<span class="punct">,</span>`;
      }
      lines.push(...childLines);
    });
    lines.push({ indent, content: html`<span class="punct">]</span>` });
    return lines;
  }
}

customElements.define('json-tree', JsonTree);
